import { useEffect, useRef, useState } from 'react';

export default function BenefitSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const benefits = [
    {
      title: '相続に伴うご負担を、安心に変える',
      description: 'ご実家の整理は、相続手続きの中でも多大な時間と精神力を要するものです。都心でお仕事をされるお子様世代に代わり、私たちがプロの技術で一括対応いたします。物理的な片付けだけでなく、ご家族の心にゆとりが生まれるよう、真心を込めてサポートします。',
      scenario: '都心にお住まいのお子様が、ご多忙な日常を止めることなく整理を完結',
      example: '実家一軒分 300,000円〜',
      image: 'https://static.readdy.ai/image/aec91cb01fdf30f00658c017aec0d8c9/84dce4044b829c8cd2e672ce401c8324.jpeg',
      icon: 'ri-home-heart-line',
      badge: '相続整理',
    },
    {
      title: '納得感を生む、公平で透明な報告',
      description: '価値あるお品物の査定額をすべて明記した、写真付きの詳細な報告書を作成いたします。離れて暮らすご兄弟や親族の方々にも、そのまま共有・ご説明いただける透明性の高い資料です。家族全員が納得し、安心して次へ進める環境を整えます。',
      scenario: '離れて暮らすご兄弟への、写真と数字に基づいた公平な共有',
      example: '詳細報告書 無料作成',
      image: 'https://static.readdy.ai/image/aec91cb01fdf30f00658c017aec0d8c9/3c50219cdbf0ef0a96297a511fb41d94.png',
      icon: 'ri-file-list-3-line',
      badge: '透明性',
    },
    {
      title: '眠っていた資産を、ご家族の未来へ',
      description: '長年大切にされてきた骨董品や貴金属には、思いがけない価値が眠っていることがあります。鑑定士・吉田が10年の経験でその真価を厳正に見極め、ご家族の資産として正しく評価いたします。整理を通じて生まれた資金が、お孫様への教育資金など、新たな家族の物語へとつながるお手伝いをします。',
      scenario: '祖父母様が遺した『真価』が、お孫様の未来を支える資金に',
      example: '骨董品・貴金属 100,000円〜',
      image: 'https://static.readdy.ai/image/aec91cb01fdf30f00658c017aec0d8c9/e1b85ce38fcb5e1892d8e864345c0fe6.jpeg',
      icon: 'ri-safe-line',
      badge: '資産活用',
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="py-32 px-6 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #fdfbf7 0%, #f5f1e8 100%)',
      }}
    >
      {/* 和紙テクスチャ背景 */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23noise)' opacity='0.05'/%3E%3C/svg%3E")`,
        }}
      ></div>

      {/* 金色アクセントライン */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#FFD700] to-transparent opacity-50"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* セクションヘッダー */}
        <div className="text-center mb-20">
          <div className="inline-block mb-6">
            <span className="text-[#00A58E] font-bold text-sm tracking-widest uppercase border-b-2 border-[#00A58E] pb-1">
              For Successors
            </span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-gray-800 mb-8" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            相続・実家整理という、ご家族の大切な節目に
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-transparent via-[#FFD700] to-transparent mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            次世代へと想いをつなぐために。買い取りかわせみは、<br />
            <strong className="text-[#00A58E]">買取から家財整理、清掃までを一括してお引き受け</strong>で、<br />
            ご多忙なご家族に代わって、ご実家の歴史を丁寧に、かつ誠実に整えます。
          </p>
        </div>

        {/* ベネフィットカード */}
        <div className="grid md:grid-cols-3 gap-10 mb-20">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className={`bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 transform ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
              style={{
                transitionDelay: `${index * 150}ms`,
              }}
            >
              {/* 画像 */}
              <div className="relative h-72 w-full overflow-hidden">
                <img
                  src={benefit.image}
                  alt={benefit.title}
                  className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute top-4 left-4 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-gray-800 px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                  {benefit.badge}
                </div>
                <div className="absolute bottom-4 right-4 bg-[#00A58E] text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                  {benefit.example}
                </div>
              </div>

              {/* コンテンツ */}
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#00A58E]/10 rounded-full mr-4">
                    <i className={`${benefit.icon} text-3xl text-[#00A58E] w-7 h-7 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                    {benefit.title}
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {benefit.description}
                </p>
                {/* シナリオバッジ */}
                <div className="bg-gradient-to-r from-[#00A58E]/5 to-teal-50 border-l-4 border-[#00A58E] px-4 py-4 rounded-r-lg">
                  <div className="flex items-start gap-2">
                    <i className="ri-user-heart-line text-[#00A58E] text-xl mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      <strong className="text-[#00A58E]">例：</strong>{benefit.scenario}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 追加ベネフィット（4項目） */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {[
            { icon: 'ri-shield-check-line', title: 'プライバシーと尊厳の守護', desc: 'ご家族のプライバシーはもちろん、お品物や建物に宿る思い出を大切に扱い、誠実に対応いたします。' },
            { icon: 'ri-calendar-check-line', title: 'ご多忙な日常に寄り添う対応', desc: '都心でお仕事をされるご家族のスケジュールに合わせ、早朝や夜間のご相談、最短での訪問にも柔軟に対応いたします。' },
            { icon: 'ri-team-line', title: 'ご親族皆様での対話を大切に', desc: 'ご兄弟や親族の皆様で内容をご確認いただき、全員が納得できる整理を。現地での立ち会いも歓迎しております。' },
            { icon: 'ri-file-shield-line', title: '専門家との連携による一括支援', desc: '提携する士業ネットワークにより、相続登記や法的なお手続きについても、窓口一つでスムーズにサポートいたします。' },
          ].map((item, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
              style={{
                transitionDelay: `${(index + 3) * 100}ms`,
              }}
            >
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#00A58E] to-teal-600 rounded-full mb-6 mx-auto">
                <i className={`${item.icon} text-3xl text-white w-8 h-8 flex items-center justify-center`}></i>
              </div>
              <h4 className="text-xl font-bold text-gray-800 text-center mb-3" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                {item.title}
              </h4>
              <p className="text-sm text-gray-600 text-center leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>

        {/* クロージングメッセージ */}
        <div className="text-center">
          <div className="inline-block bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl px-12 py-12 md:px-16 md:py-16 max-w-5xl border-2 border-[#FFD700]/30">
            <div className="flex items-center justify-center mb-8">
              <div className="w-24 h-24 flex items-center justify-center bg-gradient-to-br from-[#00A58E] to-teal-600 rounded-full">
                <i className="ri-hand-heart-line text-white text-5xl w-12 h-12 flex items-center justify-center"></i>
              </div>
            </div>
            <p className="text-4xl md:text-5xl font-bold text-gray-800 mb-8" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              ご実家の歴史を、<br className="md:hidden" />誠実な手仕事で整えます
            </p>
            <p className="text-xl text-gray-600 leading-relaxed mb-10">
              買い取りかわせみは、ただの買取業者ではございません。<br />
              ご家族が積み重ねてきた大切な時間と資産を、<br />
              敬意を持って次の世代へとつなぐ『伴走者』でありたいと考えています。
            </p>
            <div className="grid md:grid-cols-3 gap-8 mb-10">
              <div className="flex flex-col items-center justify-center gap-2 text-gray-700">
                <i className="ri-check-double-line text-[#00A58E] text-3xl w-8 h-8 flex items-center justify-center"></i>
                <span className="text-lg text-center"><strong>出張費・査定費</strong><br />不透明な費用はいただきません</span>
              </div>
              <div className="flex flex-col items-center justify-center gap-2 text-gray-700">
                <i className="ri-check-double-line text-[#00A58E] text-3xl w-8 h-8 flex items-center justify-center"></i>
                <span className="text-lg text-center"><strong>即日現金払い</strong><br />速やかな現金化と誠実な決済</span>
              </div>
              <div className="flex flex-col items-center justify-center gap-2 text-gray-700">
                <i className="ri-check-double-line text-[#00A58E] text-3xl w-8 h-8 flex items-center justify-center"></i>
                <span className="text-lg text-center"><strong>詳細報告書</strong><br />価値を記録する写真付き報告書</span>
              </div>
            </div>
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-[#FFD700] px-8 py-6 rounded-r-xl">
              <p className="text-base text-gray-700 leading-relaxed">
                <i className="ri-phone-line text-[#00A58E] mr-2 text-xl"></i>
                <strong className="text-[#00A58E]">まずはお気軽にご相談ください。</strong><br />
                吉田鑑定士が、ご家族のご状況に合わせた最適なプランをご提案いたします。
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 金色アクセントライン */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#FFD700] to-transparent opacity-50"></div>
    </section>
  );
}